"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useDockVisible } from "@/lib/home-flags-hooks";
import { TinyCloseIcon } from "@/components/icons/TinyCloseIcon";

/**
 * A toast may carry a second, quieter line and may be actionable. Both exist for
 * the reach rail (A3-T2), where a notification is a title, a body, and a place
 * to go — and where the click is the ONLY thing that proves the user was
 * reached. Both are optional, so every existing `toast('…')` call is unchanged.
 * A toast may also carry one named action beside the message (the share dialog's
 * Undo): its own button, so the message itself never runs it.
 */
interface ToastOptions {
  /** The quieter second line under the message. */
  detail?: string;
  /** Makes the toast a button; the toast dismisses itself before running this. */
  onClick?: () => void;
  /**
   * Adds an explicit dismiss control. Without one a toast can only be waited
   * out, which is fine for a receipt ("Copied") and wrong for a notification the
   * user may want to answer for: waving it away has to be something they can
   * actually DO, or the refusal never gets recorded anywhere.
   */
  onDismiss?: () => void;
  /**
   * How long this one stays, when the default is the wrong length for it. Four
   * seconds is a receipt's worth of time ("Copied"); a composer notice about a
   * person's task is a whole sentence about a finish, a failure or a question,
   * and it is the only thing a person looking at the app gets.
   */
  durationMs?: number;
  /** One named act beside the message (the share dialog's Undo). Runs once, then the toast dismisses. */
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends ToastOptions {
  id: number;
  message: string;
  isExiting?: boolean;
}

interface ToastContextValue {
  toast: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

/** How long a notice holds the screen before it is dismissed. Exported so a
 *  test measures the turn from the same number the provider spends. */
export const TOAST_DURATION_MS = 4000;

/** How long the exit animation runs, and so how long a dismissed notice stays
 *  in the DOM after it starts leaving. The `duration-300` utility on SURFACE
 *  below is this number — a Tailwind class cannot read a JS value, so the pair
 *  is held together by a test instead (ui/__tests__/Toast.test.tsx reads the
 *  class off the rendered surface). Change one, change both. */
export const TOAST_EXIT_MS = 300;

/**
 * A toast's removal clock, held outside React state because nothing about it is
 * rendered: pausing it must not repaint the toast the pointer is resting on.
 * `handle` is null exactly while it is paused.
 */
interface ToastTimer {
  handle: ReturnType<typeof setTimeout> | null;
  /** What is left to spend, as of `startedAt`. */
  remaining: number;
  startedAt: number;
}

const SURFACE =
  "pointer-events-auto rounded-12 border border-border-muted bg-bg-elevated px-4 py-2.5 text-3.5 text-fg-default shadow-lg duration-300";

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(0);
  const dockClears = useDockVisible(usePathname());

  const timers = useRef(new Map<number, ToastTimer>());
  /** Toasts whose action already ran. Not `isExiting`: a toast fading out on
   *  its own clock can still be pressed, and that press must count. */
  const acted = useRef(new Set<number>());

  const remove = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer?.handle) clearTimeout(timer.handle);
    timers.current.delete(id);

    // Mark as exiting
    setItems((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === -1 || prev[idx].isExiting) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], isExiting: true };
      return next;
    });

    // Actually remove it after animation
    setTimeout(() => {
      acted.current.delete(id);
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_EXIT_MS);
  }, []);

  const arm = useCallback(
    (id: number, ms: number) => {
      timers.current.set(id, {
        handle: setTimeout(() => remove(id), ms),
        remaining: ms,
        startedAt: Date.now(),
      });
    },
    [remove]
  );

  // The pointer resting on a toast, or the keyboard holding it, means someone is
  // reading it. A toast that vanishes mid-sentence under the cursor is the one
  // thing the user cannot ask for again, so the clock stops and picks up the
  // unspent remainder on the way out.
  const pause = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (!timer?.handle) return;
    clearTimeout(timer.handle);
    timers.current.set(id, {
      handle: null,
      remaining: Math.max(0, timer.remaining - (Date.now() - timer.startedAt)),
      startedAt: timer.startedAt,
    });
  }, []);

  const resume = useCallback(
    (id: number) => {
      const timer = timers.current.get(id);
      if (!timer || timer.handle) return;
      arm(id, timer.remaining);
    },
    [arm]
  );

  /** The same four handlers wherever a toast is drawn; onFocus/onBlur catch the child controls. */
  const held = useCallback(
    (id: number) => ({
      onMouseEnter: () => pause(id),
      onMouseLeave: () => resume(id),
      onFocus: () => pause(id),
      onBlur: () => resume(id),
    }),
    [pause, resume]
  );

  const toast = useCallback(
    (message: string, options?: ToastOptions) => {
      const id = nextId.current++;
      setItems((prev) => [...prev, { id, message, ...options }]);
      arm(id, options?.durationMs ?? TOAST_DURATION_MS);
    },
    [arm]
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) {
        if (timer.handle) clearTimeout(timer.handle);
      }
      pending.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        aria-live="polite"
        role="status"
        className={cn(
          "pointer-events-none fixed right-6 z-[80] flex flex-col gap-2",
          dockClears ? "bottom-28" : "bottom-6"
        )}
      >
        {items.map((t) => {
          const itemSurface = cn(
            SURFACE,
            t.isExiting
              ? "animate-out fade-out-0 slide-out-to-right-5 fill-mode-forwards"
              : "animate-in fade-in-0 slide-in-from-right-5"
          );

          const lines = (
            <>
              <span className="block">{t.message}</span>
              {t.detail && (
                <span className="mt-0.5 block text-fg-muted">{t.detail}</span>
              )}
            </>
          );

          // The plain case stays a plain div — no wrapper, no roles, nothing for
          // a screen reader to announce twice.
          if (!t.onClick && !t.onDismiss && !t.action) {
            return (
              <div key={t.id} className={itemSurface} {...held(t.id)}>
                {lines}
              </div>
            );
          }

          // Two acts, two controls, side by side. They cannot nest: a dismiss
          // button inside the open button is invalid markup and, worse, one act
          // firing the other.
          return (
            <div
              key={t.id}
              className={cn(itemSurface, "flex max-w-xs items-start gap-3")}
              {...held(t.id)}
            >
              {t.onClick ? (
                <button
                  type="button"
                  onClick={() => {
                    remove(t.id);
                    t.onClick?.();
                  }}
                  className="-m-1 flex-1 rounded p-1 text-left transition-colors hover:bg-bg-subtle"
                >
                  {lines}
                </button>
              ) : (
                <div className="flex-1">{lines}</div>
              )}
              {t.action && (
                <button
                  type="button"
                  onClick={() => {
                    // Runs once: a second press while the toast leaves is not a second act.
                    if (acted.current.has(t.id)) return;
                    acted.current.add(t.id);
                    remove(t.id);
                    t.action?.onClick();
                  }}
                  className="-m-1 shrink-0 rounded p-1 font-semibold underline underline-offset-4 transition-colors hover:text-fg-default"
                >
                  {t.action.label}
                </button>
              )}
              {t.onDismiss && (
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => {
                    remove(t.id);
                    t.onDismiss?.();
                  }}
                  className="-m-1 shrink-0 rounded p-1 leading-none text-fg-muted transition-colors hover:text-fg-default"
                >
                  <TinyCloseIcon />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
